/**
 * Contact export helpers: one-click vCard save + WhatsApp deep-link.
 *
 * We generate vCards on the client so it works on every phone (iOS Safari, Android
 * Chrome, WhatsApp/Contacts pickers) without a server round-trip.
 */

const _esc = (s) => String(s ?? "").replace(/\\/g, "\\\\").replace(/,/g, "\\,").replace(/;/g, "\\;").replace(/\n/g, "\\n");

/** Build a standards-compliant vCard 3.0 string from a scan parsed object. */
export function buildVCard(parsed = {}, extras = {}) {
  const name = (parsed.name || "").trim();
  const nameParts = name.split(/\s+/);
  const family = nameParts.length > 1 ? nameParts.slice(-1)[0] : "";
  const given = nameParts.length > 1 ? nameParts.slice(0, -1).join(" ") : name;
  const lines = ["BEGIN:VCARD", "VERSION:3.0"];
  lines.push(`FN:${_esc(name || parsed.company || "Contact")}`);
  lines.push(`N:${_esc(family)};${_esc(given)};;;`);
  if (parsed.company) lines.push(`ORG:${_esc(parsed.company)}`);
  if (parsed.designation) lines.push(`TITLE:${_esc(parsed.designation)}`);
  (parsed.phones || []).forEach((p, i) => {
    if (!p) return;
    lines.push(`TEL;TYPE=${i === 0 ? "CELL,VOICE,PREF" : "VOICE"}:${_esc(p)}`);
  });
  (parsed.emails || []).forEach((e, i) => {
    if (!e) return;
    lines.push(`EMAIL;TYPE=INTERNET${i === 0 ? ",PREF" : ""}:${_esc(e)}`);
  });
  (parsed.websites || []).forEach((w) => w && lines.push(`URL:${_esc(w)}`));
  const addr = extras.address || parsed.address;
  if (addr) lines.push(`ADR;TYPE=WORK:;;${_esc(addr)};;;;`);
  if (extras.note) lines.push(`NOTE:${_esc(extras.note)}`);
  lines.push(`REV:${new Date().toISOString()}`);
  lines.push("END:VCARD");
  return lines.join("\r\n");
}

/** Trigger a client-side download of the vCard. Native mobile picks up .vcf and
 *  opens the "Add contact" prompt. */
export function downloadVCard(parsed, extras = {}) {
  const vcf = buildVCard(parsed, extras);
  const safeName = (parsed?.name || parsed?.company || "contact").replace(/[^\w-]+/g, "_").slice(0, 40) || "contact";
  const blob = new Blob([vcf], { type: "text/vcard;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safeName}.vcf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Normalise an Indian phone number to digits-only for wa.me. Falls back to raw
 *  digits if we can't confidently identify country code. Empty string if unusable. */
export function waPhoneDigits(raw) {
  if (!raw) return "";
  let s = String(raw).replace(/[^\d+]/g, "");
  if (s.startsWith("+")) s = s.slice(1);
  // Strip Indian leading 0.
  if (s.length === 11 && s.startsWith("0")) s = s.slice(1);
  // If bare 10-digit Indian mobile, prepend 91.
  if (s.length === 10 && /^[6-9]/.test(s)) s = "91" + s;
  return s;
}

/** Build the wa.me deep-link. */
export function buildWhatsAppUrl(phone, text) {
  const digits = waPhoneDigits(phone);
  const params = new URLSearchParams();
  if (text) params.set("text", text);
  return digits
    ? `https://wa.me/${digits}?${params.toString()}`
    : `https://wa.me/?${params.toString()}`; // no phone — user picks contact
}

/** Default outreach template. All placeholders are string-substituted; missing
 *  values leave a graceful blank so the seller can edit before send. */
export function defaultWhatsAppMessage({ contactName, senderName, companyName, companyCity, website, event }) {
  const hi = contactName ? `Hello ${contactName},` : "Hello,";
  const meetLine = event
    ? `\nWe met at ${event}.`
    : "";
  const meLine = (senderName || companyName)
    ? `\nMy name is ${senderName || ""}${senderName && (companyName || companyCity) ? " from " : ""}${companyName || ""}${companyCity ? " " + companyCity : ""}.`
    : "";
  const web = website ? `\nCheck our collection here ${website}` : "";
  return `${hi}${meetLine}${meLine}${web}`.trim();
}
